import { c as _c } from "react/compiler-runtime";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Download, FileCheck, Search } from "lucide-react";
import { Link, useSearchParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import type { ComplianceDocument, ComplianceReportGroup } from "@osmix/contracts/compliance";
import { api, type ComplianceQueueQuery } from "@/api";
import { ComplianceReviewDialog, complianceJournalKey, complianceQueueKey, DocumentStatusBadge, NumberCheck, ruDay } from "@/components/compliance-review";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { Button } from "@osmix/ui/button";
import { Input } from "@osmix/ui/input";

/**
 * Документы о соответствии в консоли (31.10.2026): очередь проверки
 * документов, отчёт «Товары без документа в обязательных категориях» с
 * выгрузкой и журнал действий сотрудников. Правила по категориям - в разделе
 * «Категории», решение по товару - в его карточке.
 */

type Tab = "queue" | "report" | "journal";
const TABS: Tab[] = ["queue", "report", "journal"];
const STATUSES: NonNullable<ComplianceQueueQuery["status"]>[] = ["pending", "approved", "rejected", "all"];
export function CompliancePage() {
  const $ = _c(34);
  const {
    t
  } = useTranslation();
  const [params, setParams] = useSearchParams();
  let t0;
  if ($[0] !== params) {
    t0 = (TABS as string[]).includes(params.get("tab") ?? "") ? params.get("tab") as Tab : "queue";
    $[0] = params;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const tab = t0;
  let t1;
  if ($[2] !== setParams) {
    t1 = next => setParams(prev => {
      const out = new URLSearchParams(prev);
      out.set("tab", next);
      return out;
    });
    $[2] = setParams;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const setTab = t1;
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <FileCheck className="size-5" />;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t) {
    t3 = t("compliance.page.title");
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t3) {
    t4 = <h1 className="flex items-center gap-2 text-xl font-semibold">{t2} {t3}</h1>;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t) {
    t5 = t("compliance.page.intro");
    $[9] = t;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t5) {
    t6 = <p className="max-w-3xl text-sm text-muted-foreground">{t5}</p>;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t4 || $[14] !== t6) {
    t7 = <div className="space-y-1">{t4}{t6}</div>;
    $[13] = t4;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== setTab || $[17] !== t || $[18] !== tab) {
    t8 = TABS.map(key => <button key={key} type="button" role="tab" aria-selected={tab === key} data-testid={`compliance-tab-${key}`} onClick={() => setTab(key)} className={cn("-mb-px shrink-0 whitespace-nowrap border-b-2 px-4 py-2 text-sm", tab === key ? "border-primary font-medium text-foreground" : "border-transparent text-muted-foreground hover:text-foreground")}>{t(`compliance.page.tabs.${key}`)}</button>);
    $[16] = setTab;
    $[17] = t;
    $[18] = tab;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t8) {
    t9 = <div className="flex gap-1 overflow-x-auto border-b" role="tablist">{t8}</div>;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== tab) {
    t10 = tab === "queue" && <QueueTab />;
    $[22] = tab;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== tab) {
    t11 = tab === "report" && <ReportTab />;
    $[24] = tab;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== tab) {
    t12 = tab === "journal" && <JournalTab />;
    $[26] = tab;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t10 || $[29] !== t11 || $[30] !== t12 || $[31] !== t7 || $[32] !== t9) {
    t13 = <div className="space-y-4" data-testid="compliance-page">{t7}{t9}{t10}{t11}{t12}</div>;
    $[28] = t10;
    $[29] = t11;
    $[30] = t12;
    $[31] = t7;
    $[32] = t9;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  return t13;
}
function QueueTab() {
  const $ = _c(100);
  const {
    t
  } = useTranslation();
  const [status, setStatus] = useState("pending");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(null);
  let t0;
  if ($[0] !== search) {
    t0 = search.trim();
    $[0] = search;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== page || $[3] !== status || $[4] !== t0) {
    t1 = [...complianceQueueKey, status, t0, page];
    $[2] = page;
    $[3] = status;
    $[4] = t0;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== page || $[7] !== search || $[8] !== status) {
    t2 = () => api.compliance.listDocuments({
      status,
      search: search.trim() || undefined,
      page,
      pageSize: 50
    });
    $[6] = page;
    $[7] = search;
    $[8] = status;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== t1 || $[11] !== t2) {
    t3 = {
      queryKey: t1,
      queryFn: t2
    };
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const query = useQuery(t3);
  const data = query.data;
  let t4;
  if ($[13] !== data?.data) {
    t4 = data?.data ?? [];
    $[13] = data?.data;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const rows = t4;
  let t5;
  if ($[15] !== data || $[16] !== status || $[17] !== t) {
    t5 = STATUSES.map(key => <Button key={key} size="sm" variant={status === key ? "default" : "outline"} onClick={() => {
      setStatus(key);
      setPage(1);
    }} data-testid={`compliance-queue-${key}`}>{t(`compliance.page.status.${key}`)}{key !== "all" && data ? ` (${data.counts[key]})` : ""}</Button>);
    $[15] = data;
    $[16] = status;
    $[17] = t;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Search className="pointer-events-none absolute left-2.5 top-2.5 size-4 text-muted-foreground" />;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = e => {
      setSearch(e.target.value);
      setPage(1);
    };
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== t) {
    t8 = t("compliance.page.search");
    $[21] = t;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== search || $[24] !== t8) {
    t9 = <div className="relative ml-auto w-full sm:w-72">{t6}<Input value={search} onChange={t7} placeholder={t8} className="pl-8" data-testid="compliance-queue-search" /></div>;
    $[23] = search;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== t5 || $[27] !== t9) {
    t10 = <div className="flex flex-wrap items-center gap-2">{t5}{t9}</div>;
    $[26] = t5;
    $[27] = t9;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== query.error || $[30] !== query.isError) {
    t11 = query.isError && <p className="text-sm text-destructive">{(query.error as Error).message}</p>;
    $[29] = query.error;
    $[30] = query.isError;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== rows || $[33] !== t) {
    let t13;
    if ($[35] !== t) {
      t13 = doc => <button key={doc.id} type="button" onClick={() => setOpen(doc)} className="rounded-md border p-3 text-left text-sm hover:bg-muted/50" data-testid="compliance-queue-card"><div className="flex flex-wrap items-center gap-2"><DocumentStatusBadge doc={doc} /><span className="text-xs text-muted-foreground">{t(`compliance.kinds.${doc.kind}`)}</span></div><p className="mt-1 break-all font-medium">{doc.number}</p><NumberCheck doc={doc} /><p className="mt-1 text-xs text-muted-foreground">{doc.shopName || doc.shopId} · {doc.productName}</p></button>;
      $[35] = t;
      $[36] = t13;
    } else {
      t13 = $[36];
    }
    t12 = rows.map(t13);
    $[32] = rows;
    $[33] = t;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  let t13;
  if ($[37] !== t12) {
    t13 = <div className="grid gap-2 md:hidden">{t12}</div>;
    $[37] = t12;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== t) {
    t14 = t("compliance.page.col.document");
    $[39] = t;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] !== t14) {
    t15 = <TableHead>{t14}</TableHead>;
    $[41] = t14;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== t) {
    t16 = t("compliance.page.col.shop");
    $[43] = t;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[45] !== t16) {
    t17 = <TableHead>{t16}</TableHead>;
    $[45] = t16;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  let t18;
  if ($[47] !== t) {
    t18 = t("compliance.page.col.product");
    $[47] = t;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] !== t18) {
    t19 = <TableHead>{t18}</TableHead>;
    $[49] = t18;
    $[50] = t19;
  } else {
    t19 = $[50];
  }
  let t20;
  if ($[51] !== t) {
    t20 = t("compliance.page.col.validUntil");
    $[51] = t;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  let t21;
  if ($[53] !== t20) {
    t21 = <TableHead>{t20}</TableHead>;
    $[53] = t20;
    $[54] = t21;
  } else {
    t21 = $[54];
  }
  let t22;
  if ($[55] !== t) {
    t22 = t("compliance.page.col.products");
    $[55] = t;
    $[56] = t22;
  } else {
    t22 = $[56];
  }
  let t23;
  if ($[57] !== t22) {
    t23 = <TableHead>{t22}</TableHead>;
    $[57] = t22;
    $[58] = t23;
  } else {
    t23 = $[58];
  }
  let t24;
  if ($[59] !== t) {
    t24 = t("compliance.page.col.uploaded");
    $[59] = t;
    $[60] = t24;
  } else {
    t24 = $[60];
  }
  let t25;
  if ($[61] !== t24) {
    t25 = <TableHead>{t24}</TableHead>;
    $[61] = t24;
    $[62] = t25;
  } else {
    t25 = $[62];
  }
  let t26;
  if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <TableHead />;
    $[63] = t26;
  } else {
    t26 = $[63];
  }
  let t27;
  if ($[64] !== t15 || $[65] !== t17 || $[66] !== t19 || $[67] !== t21 || $[68] !== t23 || $[69] !== t25) {
    t27 = <TableHeader><TableRow>{t15}{t17}{t19}{t21}{t23}{t25}{t26}</TableRow></TableHeader>;
    $[64] = t15;
    $[65] = t17;
    $[66] = t19;
    $[67] = t21;
    $[68] = t23;
    $[69] = t25;
    $[70] = t27;
  } else {
    t27 = $[70];
  }
  let t28;
  if ($[71] !== rows || $[72] !== t) {
    let t29;
    if ($[74] !== t) {
      t29 = doc_0 => <TableRow key={doc_0.id} data-testid="compliance-queue-row"><TableCell className="max-w-[320px]"><div className="flex flex-col gap-1"><span className="flex flex-wrap items-center gap-2"><DocumentStatusBadge doc={doc_0} /><span className="text-xs text-muted-foreground">{t(`compliance.kinds.${doc_0.kind}`)}</span></span><span className="break-all font-medium">{doc_0.number}</span><NumberCheck doc={doc_0} /></div></TableCell><TableCell className="max-w-[200px] break-words">{doc_0.shopName || doc_0.shopId}</TableCell><TableCell className="max-w-[240px] break-words">{doc_0.productName}</TableCell><TableCell className={doc_0.expired ? "text-destructive" : undefined}>{doc_0.validUntil ? ruDay(doc_0.validUntil) : t("compliance.detail.unlimited")}</TableCell><TableCell className="tabular-nums">{doc_0.productCount}</TableCell><TableCell>{ruDay(doc_0.createdAt)}</TableCell><TableCell className="text-right"><Button size="sm" variant={doc_0.status === "pending" ? "default" : "outline"} onClick={() => setOpen(doc_0)} data-testid="compliance-queue-open">{doc_0.status === "pending" ? t("compliance.page.review") : t("compliance.page.open")}</Button></TableCell></TableRow>;
      $[74] = t;
      $[75] = t29;
    } else {
      t29 = $[75];
    }
    t28 = rows.map(t29);
    $[71] = rows;
    $[72] = t;
    $[73] = t28;
  } else {
    t28 = $[73];
  }
  let t29;
  if ($[76] !== t28) {
    t29 = <TableBody>{t28}</TableBody>;
    $[76] = t28;
    $[77] = t29;
  } else {
    t29 = $[77];
  }
  let t30;
  if ($[78] !== t27 || $[79] !== t29) {
    t30 = <div className="hidden md:block"><Table>{t27}{t29}</Table></div>;
    $[78] = t27;
    $[79] = t29;
    $[80] = t30;
  } else {
    t30 = $[80];
  }
  let t31;
  if ($[81] !== query.isSuccess || $[82] !== rows.length || $[83] !== status || $[84] !== t) {
    t31 = query.isSuccess && rows.length === 0 && <p className="py-8 text-center text-sm text-muted-foreground" data-testid="compliance-queue-empty">{t(status === "pending" ? "compliance.page.emptyPending" : "compliance.page.empty")}</p>;
    $[81] = query.isSuccess;
    $[82] = rows.length;
    $[83] = status;
    $[84] = t;
    $[85] = t31;
  } else {
    t31 = $[85];
  }
  let t32;
  if ($[86] !== data || $[87] !== page) {
    t32 = data && data.meta.totalPages > 1 && <div className="flex items-center justify-end gap-2 text-sm"><Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>←</Button><span>{page} / {data.meta.totalPages}</span><Button size="sm" variant="outline" disabled={page >= data.meta.totalPages} onClick={() => setPage(page + 1)}>→</Button></div>;
    $[86] = data;
    $[87] = page;
    $[88] = t32;
  } else {
    t32 = $[88];
  }
  let t33;
  if ($[89] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = () => setOpen(null);
    $[89] = t33;
  } else {
    t33 = $[89];
  }
  let t34;
  if ($[90] !== open) {
    t34 = <ComplianceReviewDialog doc={open} onClose={t33} />;
    $[90] = open;
    $[91] = t34;
  } else {
    t34 = $[91];
  }
  let t35;
  if ($[92] !== t10 || $[93] !== t11 || $[94] !== t13 || $[95] !== t30 || $[96] !== t31 || $[97] !== t32 || $[98] !== t34) {
    t35 = <div className="space-y-3">{t10}{t11}{t13}{t30}{t31}{t32}{t34}</div>;
    $[92] = t10;
    $[93] = t11;
    $[94] = t13;
    $[95] = t30;
    $[96] = t31;
    $[97] = t32;
    $[98] = t34;
    $[99] = t35;
  } else {
    t35 = $[99];
  }
  return t35;
}
const STATE_KEYS = ["missing", "pending", "expired", "rejected"] as const;
function GroupTable(t0) {
  const $ = _c(28);
  const {
    title,
    groups,
    testId
  } = t0;
  const {
    t
  } = useTranslation();
  let t1;
  if ($[0] !== title) {
    t1 = <p className="font-medium">{title}</p>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <TableHead />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t) {
    t3 = t("compliance.page.report.total");
    $[3] = t;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = <TableHead className="text-right">{t3}</TableHead>;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t) {
    t5 = t("compliance.page.report.published");
    $[7] = t;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t5) {
    t6 = <TableHead className="text-right">{t5}</TableHead>;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t) {
    t7 = STATE_KEYS.map(key => <TableHead key={key} className="text-right">{t(`compliance.page.report.state.${key}`)}</TableHead>);
    $[11] = t;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t4 || $[14] !== t6 || $[15] !== t7) {
    t8 = <TableHeader><TableRow>{t2}{t4}{t6}{t7}</TableRow></TableHeader>;
    $[13] = t4;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== groups) {
    t9 = groups.map(_temp);
    $[17] = groups;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t9) {
    t10 = <TableBody>{t9}</TableBody>;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10 || $[22] !== t8) {
    t11 = <div className="overflow-x-auto"><Table>{t8}{t10}</Table></div>;
    $[21] = t10;
    $[22] = t8;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t1 || $[25] !== t11 || $[26] !== testId) {
    t12 = <div className="min-w-0 space-y-2 rounded-md border p-3" data-testid={testId}>{t1}{t11}</div>;
    $[24] = t1;
    $[25] = t11;
    $[26] = testId;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  return t12;
}
function _temp(group) {
  return <TableRow key={group.key}><TableCell className="max-w-[220px] break-words">{group.label}</TableCell><TableCell className="text-right tabular-nums">{group.total}</TableCell><TableCell className="text-right tabular-nums">{group.published}</TableCell>{STATE_KEYS.map(key_0 => <TableCell key={key_0} className="text-right tabular-nums">{group[key_0] || ""}</TableCell>)}</TableRow>;
}
function ReportTab() {
  const {
    t
  } = useTranslation();
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const query = useQuery({
    queryKey: ["compliance-report", status],
    queryFn: () => api.compliance.report(status ? {
      status
    } : {})
  });
  const report = query.data;
  const items = useMemo(() => (report?.items ?? []).slice(0, 300), [report]);
  const download = async () => {
    setBusy(true);
    setError(null);
    try {
      const file = await api.compliance.reportCsv(status ? {
        status
      } : {});
      const url = URL.createObjectURL(file.blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return <div className="space-y-3" data-testid="compliance-report">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm text-muted-foreground">{t("compliance.page.report.intro")}</p>
        <div className="ml-auto flex flex-wrap gap-2">
          {["", "published", "moderation", "blocked"].map(key => <Button key={key || "all"} size="sm" variant={status === key ? "default" : "outline"} onClick={() => setStatus(key)}>
              {t(`compliance.page.report.filter.${key || "all"}`)}
            </Button>)}
          <Button size="sm" variant="outline" onClick={() => void download()} disabled={busy} data-testid="compliance-report-export">
            <Download className="size-4" /> {t("compliance.page.report.export")}
          </Button>
        </div>
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
      {report && <p className="text-sm font-medium" data-testid="compliance-report-total">
          {t("compliance.page.report.count", {
        count: report.total
      })}
        </p>}
      {report && report.total > 0 && <>
          <div className="grid gap-3 xl:grid-cols-2">
            <GroupTable title={t("compliance.page.report.byShop")} groups={report.byShop} testId="compliance-report-shops" />
            <GroupTable title={t("compliance.page.report.byCategory")} groups={report.byCategory} testId="compliance-report-categories" />
          </div>
          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("compliance.page.col.product")}</TableHead>
                  <TableHead>{t("compliance.page.col.shop")}</TableHead>
                  <TableHead>{t("compliance.page.col.category")}</TableHead>
                  <TableHead>{t("compliance.page.col.status")}</TableHead>
                  <TableHead>{t("compliance.page.col.document")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map(item => <TableRow key={item.productId}>
                    <TableCell className="max-w-[260px]">
                      <Link to={`/products/${encodeURIComponent(item.productId)}`} className="break-words hover:underline">
                        {item.name}
                      </Link>
                      <span className="block text-xs text-muted-foreground">{item.article}</span>
                    </TableCell>
                    <TableCell className="max-w-[180px] break-words">
                      {item.shopName || item.shopId}
                    </TableCell>
                    <TableCell>{item.category ?? ""}</TableCell>
                    <TableCell>
                      {t(`products.status.${item.status}`, {
                  defaultValue: item.status
                })}
                    </TableCell>
                    <TableCell>
                      {t(`compliance.page.report.state.${item.state}`, {
                  defaultValue: item.state
                })}
                      {item.documentNumber && <span className="block break-all text-xs text-muted-foreground">
                          {item.documentNumber}
                        </span>}
                    </TableCell>
                  </TableRow>)}
              </TableBody>
            </Table>
          </div>
          {report.total > items.length && <p className="text-xs text-muted-foreground">
              {t("compliance.page.report.more", {
          shown: items.length,
          total: report.total
        })}
            </p>}
        </>}
      {report && report.total === 0 && <p className="py-8 text-center text-sm text-muted-foreground">
          {t("compliance.page.report.empty")}
        </p>}
    </div>;
}
function JournalTab() {
  const $ = _c(46);
  const {
    t
  } = useTranslation();
  const [page, setPage] = useState(1);
  let t0;
  if ($[0] !== page) {
    t0 = {
      queryKey: [...complianceJournalKey, page],
      queryFn: () => api.compliance.journal({
        page,
        pageSize: 50
      })
    };
    $[0] = page;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const query = useQuery(t0);
  let t1;
  if ($[2] !== query.data?.data) {
    t1 = query.data?.data ?? [];
    $[2] = query.data?.data;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const rows = t1;
  const meta = query.data?.meta;
  let t2;
  if ($[4] !== t) {
    t2 = t("compliance.page.journal.when");
    $[4] = t;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t2) {
    t3 = <TableHead>{t2}</TableHead>;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t) {
    t4 = t("compliance.page.journal.who");
    $[8] = t;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t4) {
    t5 = <TableHead>{t4}</TableHead>;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t) {
    t6 = t("compliance.page.journal.what");
    $[12] = t;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t6) {
    t7 = <TableHead>{t6}</TableHead>;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t) {
    t8 = t("compliance.page.journal.details");
    $[16] = t;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t8) {
    t9 = <TableHead>{t8}</TableHead>;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t3 || $[21] !== t5 || $[22] !== t7 || $[23] !== t9) {
    t10 = <TableHeader><TableRow>{t3}{t5}{t7}{t9}</TableRow></TableHeader>;
    $[20] = t3;
    $[21] = t5;
    $[22] = t7;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== rows || $[26] !== t) {
    let t12;
    if ($[28] !== t) {
      t12 = row => {
        const number = typeof row.metadata.number === "string" ? row.metadata.number : null;
        return <TableRow key={row.id}><TableCell className="whitespace-nowrap">{ruDay(row.createdAt)}{" "}{new Date(row.createdAt).toLocaleTimeString("ru-RU", {
              hour: "2-digit",
              minute: "2-digit"
            })}</TableCell><TableCell>{row.actorName}</TableCell><TableCell>{t(`compliance.page.journal.actions.${row.action.replace(/\./g, "_")}`, {
              defaultValue: row.action
            })}</TableCell><TableCell className="max-w-[360px] break-words text-xs text-muted-foreground">{[number ? `№ ${number}` : null, row.reason].filter(Boolean).join(" \xB7 ")}{row.entityKind === "product" && row.entityId && !row.entityId.startsWith("compliance:") && <Link to={`/products/${encodeURIComponent(row.entityId)}`} className="ml-1 hover:underline">{t("compliance.page.journal.product")}</Link>}</TableCell></TableRow>;
      };
      $[28] = t;
      $[29] = t12;
    } else {
      t12 = $[29];
    }
    t11 = rows.map(t12);
    $[25] = rows;
    $[26] = t;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[30] !== t11) {
    t12 = <TableBody>{t11}</TableBody>;
    $[30] = t11;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== t10 || $[33] !== t12) {
    t13 = <div className="overflow-x-auto"><Table>{t10}{t12}</Table></div>;
    $[32] = t10;
    $[33] = t12;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== query.isSuccess || $[36] !== rows.length || $[37] !== t) {
    t14 = query.isSuccess && rows.length === 0 && <p className="py-8 text-center text-sm text-muted-foreground">{t("compliance.page.journal.empty")}</p>;
    $[35] = query.isSuccess;
    $[36] = rows.length;
    $[37] = t;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== meta || $[40] !== page) {
    t15 = meta && meta.totalPages > 1 && <div className="flex items-center justify-end gap-2 text-sm"><Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>←</Button><span>{page} / {meta.totalPages}</span><Button size="sm" variant="outline" disabled={page >= meta.totalPages} onClick={() => setPage(page + 1)}>→</Button></div>;
    $[39] = meta;
    $[40] = page;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  let t16;
  if ($[42] !== t13 || $[43] !== t14 || $[44] !== t15) {
    t16 = <div className="space-y-3" data-testid="compliance-journal">{t13}{t14}{t15}</div>;
    $[42] = t13;
    $[43] = t14;
    $[44] = t15;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  return t16;
}